import { OrderDetails } from "entities/order-details";
import { OrderDetailsLineItem, OrderLineItemDeliveryStatus } from "entities/order-details-line-item";

export const calculateShippingStatus = (
    lineItem: OrderDetailsLineItem,
    orderDetails: OrderDetails,
    shippingManagementEnabled: boolean
): OrderLineItemDeliveryStatus => {

    const invoices = orderDetails.invoiceNumbers;
    const hasFreightTrackingNumber = orderDetails.freightDetails?.some(d => d.lineItemNumber === (parseInt(lineItem.lineItemNumber, 10) / 10) && d.trackingId);
    const isFreightShipped = lineItem.deliveryOption === 'freight' && hasFreightTrackingNumber;
    const freightDeliveryStatus = orderDetails.freightDetails?.find(f => f.lineItemNumber === (parseInt(lineItem.lineItemNumber, 10) / 10))?.trackingText;
    const deliveryOption = lineItem.deliveryOption;

    // DeliveryOptions of fcKnown, adHoc , selfPick , WillCall or shipDirect
    // will have a deliveryStatus of NotApplicable (N/A)
    if (['fcKnown', 'adHoc', 'selfPick', 'willCall', 'shipDirect'].includes(deliveryOption)) return 'notApplicable';
    // Shipped but no delivery Status
    if (isFreightShipped && freightDeliveryStatus?.length <= 0) return 'shipped';
    // Shipped and we DO have some delivery status
    if (isFreightShipped && freightDeliveryStatus?.length >= 0) return freightDeliveryStatus as OrderLineItemDeliveryStatus;

    if (!isFreightShipped && ['ready'].includes(lineItem.deliveryStatus)) return 'preparing';
    if (!isFreightShipped && lineItem.deliveryStatus === 'enRoute') return 'enRoute';
    if (!isFreightShipped && lineItem.deliveryStatus === 'delivered') return 'delivered';
    if ((deliveryOption === 'freight' || deliveryOption === 'rtcDelivery') && !shippingManagementEnabled) return 'notApplicable';

    // NOT Shipped but DOES have deliveryOption of 'willCall'
    if (!isFreightShipped && lineItem.isCore && deliveryOption !== 'willCall') return 'notApplicable';

    // Not Shipped and has delivery type NOT 'willCall'
    if (!isFreightShipped && lineItem.deliveryOption !== 'willCall') {
        return lineItem.deliveryStatus;
    }

    // RTC or Freight will only have delivery status of N/A if the order is in a branch with FF off.
    if (['freight', 'rtcDelivery'].includes(lineItem.deliveryOption) && !shippingManagementEnabled) {
        return 'notApplicable' as OrderLineItemDeliveryStatus;
    }

    // Invoiced and Not freight
    if (invoices.length > 0 && lineItem.deliveryOption !== 'freight') {
        return 'notApplicable' as OrderLineItemDeliveryStatus;
    }

    return 'preparing';
}

// Shipping status label mappings with conditional labels for internal vs external users
const STATUS_LABELS: Record<OrderLineItemDeliveryStatus, { external: string; internal: string }> = {
    notApplicable: { external: 'N/A', internal: 'N/A' },
    delivered: { external: 'Delivered', internal: 'Delivered' },
    enRoute: { external: 'Out for Delivery', internal: 'En Route' },
    preparing: { external: 'Preparing Order', internal: 'Preparing Order' },
    notDelivered: { external: 'Not to be Delivered', internal: 'Not to be Delivered' },
    shipped: { external: 'Shipped', internal: 'Shipped' },
    notPicked: { external: 'Call for Status', internal: 'Not Picked' },
    picked: { external: 'Preparing Order', internal: 'Preparing Order' },
    ready: { external: 'Preparing Order', internal: 'Preparing Order' },
    pickingInProgress: { external: 'Preparing Order', internal: 'Not Picked' },
    readyForDelivery: { external: 'Preparing Order', internal: 'Preparing Order' },
    scheduled: { external: 'Scheduled', internal: 'Scheduled' },
    outForDelivery: { external: 'Out for Delivery', internal: 'Out for Delivery' },
    zeroPicked: { external: 'Call for Status', internal: 'Zero Picked' },
    customerPickedUp: { external: 'Customer Picked Up', internal: 'Customer Picked Up' }
};

export const getShippingStatusLabel = (
    status: OrderLineItemDeliveryStatus,
    isInternalUser: boolean
): string => {
    const statusConfig = STATUS_LABELS[status];
    if (!statusConfig) {
        return 'N/A';
    }
    return isInternalUser ? statusConfig.internal : statusConfig.external;
}
